// ReportTableMetricsSummary.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
    Button,
    Box,
    Container,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Skeleton,
    Stack,
    Table,
    TableBody,
    Tooltip,
    Typography,
    TextField,
} from "@mui/material";

import HoverPaper from "../HoverPaper";
import CheckShow from "../CheckShow";
import { CheckHideBool } from "../CheckHide";
import TooltipComparison from "../TooltipComparison";

import SmallPercentage from "../../SmallPercentage";
import NoDataRow from "../../NoDataRow";
import InfoIcon from "../../InfoIcon";
import EventsTimeCarousel from "../../events/EventsTimeCarousel";

import { DEFAULT_TARGET, getComparisonMetaUi } from "../../../helpers/targets";
import Icons from "../../../helpers/icons";
import Colours from "../../../helpers/colours";
import {
    COMPARISON_FILTER,
    DATATYPE_CHECKIN,
    DATATYPE_RESDATE,
    DEFAULT_COMPARISON,
    DISPLAY_ELEMENT_HELPER,
    getMetricName,
} from "../../../helpers/filters";
import { fns } from "../../../helpers/common";

import { ARO_ADMIN } from "../../../configuration";
import ReportTableOverviewMetricByMonth from "./ReportTableOverviewMetricByMonth";

//   _____ _                           _
//  | ____| | ___ _ __ ___   ___ _ __ | |_
//  |  _| | |/ _ \ '_ ` _ \ / _ \ '_ \| __|
//  | |___| |  __/ | | | | |  __/ | | | |_
//  |_____|_|\___|_| |_| |_|\___|_| |_|\__|
export const DataElement = ({
    name,
    value,
    valueOld,
    comparisonMeta = null,
    fromDay,
    toDay,
    comparison,
    comparisonCustom,
    js,
    place,
    hidePercentage = false,
    tooltipRenderers = {},
    disableTooltipFor = [],
    metrics = DISPLAY_ELEMENT_HELPER,
}) => {
    const { id: hotelID } = useSelector((state) => state.hotelID);
    const [selected, setSelected] = useState(false);

    const isTargetApplied = comparisonMeta?.isTarget === true;

    // Build the display label exactly as header uses it
    const displayLabel = fns(
        hotelID,
        getMetricName(name, place, metrics) ?? name,
        (metrics[name] ?? {}).headerFormat,
    );

    // Decide if tooltip is shown or not
    // 1) Config via DISPLAY_ELEMENT_HELPER[metric]?.tooltip === false
    const configDisables = (metrics[name] ?? {}).tooltip === false;
    // 2) Prop disables (accept both metric key and display label)
    const propDisables =
        disableTooltipFor.includes(name) ||
        disableTooltipFor.includes(displayLabel);

    const showTooltip = !(configDisables || propDisables);

    // Find a custom renderer (accept both keys)
    const customRenderer =
        tooltipRenderers[name] ?? tooltipRenderers[displayLabel] ?? null;

    const tooltipExtra = (metrics[name] ?? {}).tooltipExtra ?? null;

    if (value !== null)
        return (
            <TooltipComparison
                placeForMetricName={place}
                metricName={name}
                comparisonType={comparison}
                comparisonCustom={comparisonCustom}
                fromDay={fromDay}
                toDay={toDay}
                value={value}
                valueOld={valueOld}
                comparisonMeta={comparisonMeta}
                dataType={js?.dataType}
                setSelected={setSelected}
                show={showTooltip}
                renderContent={customRenderer}
                extraContent={tooltipExtra}
                valueFormat={(metrics[name] ?? {}).format ?? {}}
            >
                <Stack
                    direction="column"
                    justifyContent="center"
                    alignItems="stretch"
                    spacing={1}
                    p={1}
                    sx={{
                        backgroundColor: selected ? "white" : null,
                        border: selected
                            ? "1px solid lightgrey"
                            : "1px solid transparent",
                        borderRadius: 1,
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Typography>{displayLabel}</Typography>
                    </Stack>

                    <Stack
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={4}
                    >
                        {metrics[name]?.valueElement ?? (
                            <Typography fontWeight="bold">
                                {fns(
                                    hotelID,
                                    value,
                                    (metrics[name] ?? {}).format ?? {},
                                )}
                            </Typography>
                        )}
                        {!hidePercentage && (
                            <Stack direction={"row"} spacing={0.5}>
                                <SmallPercentage
                                    brackets={false}
                                    tooltip={false}
                                    bubble={true}
                                    bubbleRound={true}
                                    actual={value}
                                    old={valueOld}
                                    showFormula={false}
                                    isInverted={
                                        (metrics[name] ?? {}).upside ?? false
                                    }
                                />
                                {isTargetApplied && (
                                    <Box
                                        sx={{
                                            px: 0.8,
                                            py: 0.15,
                                            borderRadius: "999px",
                                            fontSize: "0.68em",
                                            fontWeight: "bold",
                                            lineHeight: 1.4,
                                            color: Colours.primary,
                                            backgroundColor: `${Colours.primary}15`,
                                            border: `1px solid ${Colours.primary}30`,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        T
                                    </Box>
                                )}
                            </Stack>
                        )}
                    </Stack>
                </Stack>
            </TooltipComparison>
        );
    else
        return (
            <Stack
                direction="column"
                justifyContent="flex-start"
                alignItems="stretch"
                spacing={0}
            >
                <Skeleton width={100} />

                <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    spacing={4}
                >
                    <Skeleton width={50} />
                    <Skeleton
                        variant="rounded"
                        sx={{ borderRadius: "45px" }}
                        width={45}
                        height={25}
                    />
                </Stack>
            </Stack>
        );
};

//   _____  _    ____  _     _____
//  |_   _|/ \  | __ )| |   | ____|
//    | | / _ \ |  _ \| |   |  _|
//    | |/ ___ \| |_) | |___| |___
//    |_/_/   \_\____/|_____|_____|
const PLACE = "FiltersTableReport";
export const REPORT_TABLE_METRICS_SUMMARY = "REPORT_TABLE_METRICS_SUMMARY";

export const getVisibleMetricsSummary = ({
    js,
    groupResult,
    hotelID,
    metrics = DISPLAY_ELEMENT_HELPER,
}) => {
    const ordered = (js?.fieldsOrder ?? []).filter(
        (metric) =>
            metric !== "empty" &&
            metric in (groupResult ?? {}) &&
            !(js?.hideFields ?? []).includes(metric) &&
            (CheckHideBool("Vouchers", hotelID) ||
                !((metrics[metric] ?? {}).isVoucher ?? false)),
    );

    const remaining = Object.keys(groupResult ?? {}).filter(
        (metric) =>
            !(js?.fieldsOrder ?? []).includes(metric) &&
            !(js?.hideFields ?? []).includes(metric) &&
            (CheckHideBool("Vouchers", hotelID) ||
                !((metrics[metric] ?? {}).isVoucher ?? false)),
    );

    return [...ordered, ...remaining];
};

const formatSummaryMetricValue = ({ hotelID, value, helper }) => {
    if (value === null || value === undefined || value === "") return "—";
    return fns(hotelID, value, helper?.format ?? {});
};

const buildDeltaCell = ({ actual, comparison, inverted = false }) => {
    if (
        actual === null ||
        actual === undefined ||
        comparison === null ||
        comparison === undefined ||
        Number(comparison) === 0
    ) {
        return { value: "—" };
    }

    const percentage = Math.round(
        ((Number(actual) - Number(comparison)) / Number(comparison)) * 100,
    );

    return {
        value: `${percentage}%`,
        style: {
            trafficLight: {
                value: percentage,
                inverted,
            },
        },
    };
};

export const buildMetricsSummaryExcelSections = ({
    data,
    js,
    hotelID,
    report,
}) => {
    const group = Object.values(data ?? {})[0];

    if (!group?.result || Object.keys(group.result).length === 0) {
        return [];
    }

    const metrics =
        report.metrics ?? report.props?.metrics ?? DISPLAY_ELEMENT_HELPER;
    const place = report.props?.place ?? PLACE;
    const visibleMetrics = getVisibleMetricsSummary({
        js,
        groupResult: group.result,
        hotelID,
        metrics,
    });

    if (visibleMetrics.length === 0) {
        return [];
    }

    const comparisonLabel =
        COMPARISON_FILTER[js?.filters?.Comparison ?? DEFAULT_COMPARISON]
            ?.label ?? "Comparison";

    return [
        {
            id: "metrics-summary",
            kind: "table",
            title: report.props?.title ?? report.title ?? "Summary",
            columns: [
                { key: "metric", label: "Metric" },
                { key: "current", label: "Current" },
                { key: "reference", label: "Reference" },
                { key: "referenceType", label: "Reference type" },
                { key: "delta", label: "+/- (%)" },
            ],
            rows: visibleMetrics.map((metric) => {
                const helper = metrics[metric] ?? {};
                const current = group.result?.[metric];
                const reference = group.resultOneYearBefore?.[metric];
                const comparisonMeta = getComparisonMetaUi(
                    group.comparisonMetaByMetric?.[metric],
                );

                return {
                    metric: fns(
                        hotelID,
                        getMetricName(metric, place, metrics) ??
                            helper.short ??
                            metric,
                        helper.headerFormat,
                    ),
                    current: formatSummaryMetricValue({
                        hotelID,
                        value: current,
                        helper,
                    }),
                    reference: formatSummaryMetricValue({
                        hotelID,
                        value: reference,
                        helper,
                    }),
                    referenceType: comparisonMeta?.isTarget
                        ? "Target"
                        : comparisonLabel,
                    delta: buildDeltaCell({
                        actual: current,
                        comparison: reference,
                        inverted: helper.upside ?? false,
                    }),
                };
            }),
        },
    ];
};

const ReportTableMetricsSummary = ({
    data,
    js,
    PDF = false,
    hideExtra = false,
    hideEvents = true,
    processFn = null,
    title,
    datesTitle = "",
    place = PLACE,
    tooltipRenderers = {},
    disableTooltipFor = [],
    metrics = DISPLAY_ELEMENT_HELPER,
}) => {
    const [js_, setJs_] = useState(js);
    const { id: hotelID } = useSelector((state) => state.hotelID);

    const [openHidePanel, setOpenHidePanel] = useState(false);
    const [hideFieldsSearch, setHideFieldsSearch] = useState("");
    const [group, setGroup] = useState({ keys: [], result: {} });
    const [groupNumber, setGroupNumber] = useState(0);
    const [viewMode, setViewMode] = useState("grid");

    const { dataSimple, dataTargets } = useSelector((state) => state);

    const visibleMetrics = useMemo(
        () =>
            getVisibleMetricsSummary({
                js: js_,
                groupResult: group.result,
                hotelID,
                metrics,
            }),
        [js_, group.result, hotelID, metrics],
    );

    const isTargetEnabled =
        (js_.filters.Target ?? DEFAULT_TARGET) !== DEFAULT_TARGET;

    const comparisonLabel =
        COMPARISON_FILTER[js_.filters.Comparison ?? DEFAULT_COMPARISON]
            ?.label ?? "Same time last year";

    useEffect(() => {
        if (Object.keys(data).length > 0) {
            if (groupNumber >= Object.keys(data).length) setGroupNumber(0);
            if (groupNumber < Object.values(data).length) {
                setGroup(
                    Object.values(data)[groupNumber] ?? {
                        keys: [],
                        result: {},
                    },
                );
            }
        }
    }, [data, js_, groupNumber]);

    // Uncomment this to easily test email last month
    // useEffect(() => {
    //     let text =
    //         `${group.result.revenueTotal}\n` +
    //         `${group.result.revenueRoom}\n` +
    //         `${group.result.revenueRoomAndExtras}\n` +
    //         `${group.result.revenueExtras}\n` +
    //         `${group.result.bookingsRooms}\n` +
    //         `${group.result.roomNightsTotal}\n` +
    //         `${group.result.bookingsCancelled}\n` +
    //         `${group.result.ABWTotal}\n` +
    //         `${group.result.ALoSTotal}\n` +
    //         `${group.result.ADRTotal}\n` +
    //         `${group.result.revenueVouchers}\n` +
    //         `${group.result.vouchersBookings}\n` +
    //         `${group.result.vouchersCount}\n`;
    //     console.log(text);
    //     navigator.clipboard.writeText(text);
    // }, [group]);

    useEffect(() => {
        setJs_(js);
    }, [js]);

    return (
        <>
            {title && (
                <Typography fontWeight={"bold"} sx={{ pt: 2 }}>
                    {title}
                </Typography>
            )}
            <Container
                maxWidth={false}
                component={HoverPaper}
                sx={{
                    p: 2,
                    backgroundColor: Colours.notificationCard,
                }}
            >
                {Object.keys(group.result).length > 0 && // HERE TODO: review
                Object.keys(js_).length > 2 &&
                Object.keys(data).length > 0 ? (
                    <>
                        {Object.values(data).length > 1 && (
                            <Stack
                                direction="row"
                                justifyContent="flex-start"
                                alignItems="center"
                                spacing={1}
                            >
                                <IconButton
                                    size="small"
                                    disabled={groupNumber === 0}
                                    onClick={() =>
                                        setGroupNumber(groupNumber - 1)
                                    }
                                >
                                    <Icons.ArrowLeft />
                                </IconButton>
                                <Typography fontSize="0.8em">
                                    {groupNumber + 1}/
                                    {Object.values(data).length}
                                </Typography>
                                <IconButton
                                    size="small"
                                    disabled={
                                        groupNumber >=
                                        Object.values(data).length - 1
                                    }
                                    onClick={() =>
                                        setGroupNumber(groupNumber + 1)
                                    }
                                >
                                    <Icons.ArrowRight />
                                </IconButton>
                                <Grid container columnSpacing={1}>
                                    {(group.keys ?? []).map((e) => (
                                        <Grid item>
                                            <Typography
                                                fontSize="0.9em"
                                                key={e[0]}
                                            >
                                                <b>
                                                    {e[0] ?? "NONE (Voucher)"}
                                                </b>
                                                : {e[1] ?? "NONE (Voucher)"}
                                            </Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Stack>
                        )}
                        {(!hideExtra || !!processFn) && (
                            <>
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    spacing={1}
                                >
                                    {!hideExtra && (
                                        <Typography
                                            sx={{
                                                fontSize: "0.8em",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {datesTitle != ""
                                                ? `${datesTitle}: `
                                                : js_.dataType ===
                                                    DATATYPE_RESDATE
                                                  ? "Booked: "
                                                  : js_.dataType ===
                                                      DATATYPE_CHECKIN
                                                    ? "Checked in: "
                                                    : "Checked out: "}
                                            {new Date(
                                                js_.fromDay,
                                            ).toLocaleDateString("en-IE", {
                                                year: "numeric",
                                                month: "short",
                                                day: "2-digit",
                                            })}{" "}
                                            -{" "}
                                            {new Date(
                                                js_.toDay,
                                            ).toLocaleDateString("en-IE", {
                                                year: "numeric",
                                                month: "short",
                                                day: "2-digit",
                                            })}
                                        </Typography>
                                    )}
                                    {processFn && (
                                        <Box sx={{ ml: "auto" }}>
                                            <Tooltip
                                                title={
                                                    viewMode === "grid"
                                                        ? "Table view"
                                                        : "Grid view"
                                                }
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        setViewMode((v) =>
                                                            v === "grid"
                                                                ? "table"
                                                                : "grid",
                                                        )
                                                    }
                                                >
                                                    {viewMode === "grid" ? (
                                                        <Icons.Table />
                                                    ) : (
                                                        <Icons.DashBoard />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )}
                                </Stack>

                                <Divider
                                    sx={{
                                        marginY: 2,
                                        borderColor: Colours.navbarBottomLine,
                                    }}
                                />
                            </>
                        )}

                        {viewMode === "grid" ? (
                            <Grid container rowSpacing={4} columnSpacing={2}>
                                {Object.values(group.result).length === 0 &&
                                    [...Array(8).keys()].map((e, i) => (
                                        <Grid item key={i} xs={6} md={3}>
                                            <DataElement
                                                value={null}
                                                place={place}
                                                metrics={metrics}
                                            />
                                        </Grid>
                                    ))}
                                {/* Sorted metrics */}
                                {js_.fieldsOrder.map((metric, index) => {
                                    if (
                                        !CheckHideBool("Vouchers", hotelID) &&
                                        ((metrics[metric] ?? {}).isVoucher ??
                                            false)
                                    )
                                        return;
                                    if (metric === "empty") {
                                        return (
                                            <Grid
                                                item
                                                key={metric + index}
                                                xs={6}
                                                md={3}
                                            />
                                        );
                                    }
                                    if (
                                        metric in group.result &&
                                        !js_.hideFields.includes(metric)
                                    ) {
                                        return (
                                            <Grid
                                                item
                                                key={metric}
                                                xs={6}
                                                md={3}
                                            >
                                                <DataElement
                                                    metrics={metrics}
                                                    name={metric}
                                                    value={group.result[metric]}
                                                    valueOld={
                                                        (group.resultOneYearBefore ??
                                                            {})[metric]
                                                    }
                                                    comparisonMeta={getComparisonMetaUi(
                                                        group
                                                            .comparisonMetaByMetric?.[
                                                            metric
                                                        ],
                                                    )}
                                                    percentageDirection={false}
                                                    fromDay={js_.fromDay}
                                                    toDay={js_.toDay}
                                                    comparison={
                                                        js_.filters
                                                            .Comparison ??
                                                        DEFAULT_COMPARISON
                                                    }
                                                    comparisonCustom={
                                                        js_.filters
                                                            .ComparisonCustom
                                                    }
                                                    place={place}
                                                    hidePercentage={(
                                                        js_.hidePercentageFor ??
                                                        []
                                                    ).includes(metric)}
                                                    tooltipRenderers={
                                                        tooltipRenderers
                                                    }
                                                    disableTooltipFor={
                                                        disableTooltipFor
                                                    }
                                                />
                                            </Grid>
                                        );
                                    }
                                })}

                                {/* Not sorted metrics */}
                                {Object.entries(group.result ?? {}).map(
                                    ([metric, value]) => {
                                        if (
                                            !CheckHideBool(
                                                "Vouchers",
                                                hotelID,
                                            ) &&
                                            ((metrics[metric] ?? {})
                                                .isVoucher ??
                                                false)
                                        )
                                            return;
                                        if (
                                            !js_.hideFields.includes(metric) &&
                                            !js_.fieldsOrder.includes(metric) //HERE
                                        ) {
                                            return (
                                                <Grid
                                                    item
                                                    key={metric}
                                                    xs={6}
                                                    md={3}
                                                >
                                                    <DataElement
                                                        metrics={metrics}
                                                        name={metric}
                                                        value={value}
                                                        valueOld={
                                                            (group.resultOneYearBefore ??
                                                                {})[metric]
                                                        }
                                                        comparisonMeta={getComparisonMetaUi(
                                                            group
                                                                .comparisonMetaByMetric?.[
                                                                metric
                                                            ],
                                                        )}
                                                        percentageDirection={
                                                            false
                                                        }
                                                        fromDay={js_.fromDay}
                                                        toDay={js_.toDay}
                                                        comparison={
                                                            js_.filters
                                                                .Comparison ??
                                                            DEFAULT_COMPARISON
                                                        }
                                                        comparisonCustom={
                                                            js_.filters
                                                                .ComparisonCustom
                                                        }
                                                        place={place}
                                                        hidePercentage={(
                                                            js_.hidePercentageFor ??
                                                            []
                                                        ).includes(metric)}
                                                        tooltipRenderers={
                                                            tooltipRenderers
                                                        }
                                                        disableTooltipFor={
                                                            disableTooltipFor
                                                        }
                                                    />
                                                </Grid>
                                            );
                                        } else return null;
                                    },
                                )}
                            </Grid>
                        ) : (
                            <ReportTableOverviewMetricByMonth
                                group={group}
                                js={js_}
                                processFn={processFn}
                                rawData={group.data}
                                dataSimple={dataSimple}
                                dataTargets={dataTargets}
                                visibleMetrics={visibleMetrics}
                                hotelID={hotelID}
                                place={place}
                                metrics={metrics}
                                PDF={PDF}
                                comparisonLabel={comparisonLabel}
                            />
                        )}

                        {!PDF &&
                            !hideExtra &&
                            !hideEvents &&
                            viewMode === "grid" && (
                                <EventsTimeCarousel js={js} />
                            )}

                        {hideExtra ? (
                            <></>
                        ) : (
                            <>
                                <Divider
                                    sx={{
                                        marginY: 2,
                                        borderColor: Colours.navbarBottomLine,
                                    }}
                                />
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Stack
                                        direction="row"
                                        justifyContent="flex-end"
                                        alignItems="center"
                                    >
                                        {Array.from({ length: 2 }).map(
                                            (_, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        backgroundColor:
                                                            index === 0
                                                                ? `${Colours.trafficLightGreen}20`
                                                                : `${Colours.trafficLightRed}20`,
                                                        color:
                                                            index === 0
                                                                ? "#1B7B3E"
                                                                : "#D11D1D",
                                                        py: ".1em",
                                                        px: ".5em",
                                                        borderRadius: "50px",
                                                        mr: ".5em",
                                                    }}
                                                >
                                                    {index === 0 ? (
                                                        <Icons.ArrowFullUp
                                                            sx={{
                                                                fontSize:
                                                                    "0.9em",
                                                            }}
                                                        />
                                                    ) : (
                                                        <Icons.ArrowFullDown
                                                            sx={{
                                                                fontSize:
                                                                    "0.9em",
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            ),
                                        )}
                                        <Typography
                                            sx={{
                                                fontSize: "0.8em",
                                                ml: "2em",
                                            }}
                                        >
                                            {isTargetEnabled
                                                ? `Comparison is against the target when available; otherwise, it is against the ${COMPARISON_FILTER[
                                                      js_.filters.Comparison ??
                                                          DEFAULT_COMPARISON
                                                  ].label.toLowerCase()}.`
                                                : `Comparison is against the ${COMPARISON_FILTER[
                                                      js_.filters.Comparison ??
                                                          DEFAULT_COMPARISON
                                                  ].label.toLowerCase()}.`}
                                        </Typography>
                                        <InfoIcon
                                            description={`The comparison percentage is calculated by taking the (selected period minus ${comparisonLabel.toLowerCase()}) divided by ${comparisonLabel.toLowerCase()} x 100.

For example:
    Revenue:
        Selected period = â‚¬26915
        ${comparisonLabel} = â‚¬30500
        (â‚¬26915 - â‚¬30500)/â‚¬30500 x 100 = -12%`}
                                        />
                                    </Stack>
                                    <CheckShow rolesThatCanSee={ARO_ADMIN}>
                                        <Button
                                            startIcon={<Icons.EyeOff />}
                                            onClick={() => {
                                                setHideFieldsSearch("");
                                                setOpenHidePanel(true);
                                            }}
                                        >
                                            {js_.hideFields.length}
                                        </Button>
                                    </CheckShow>
                                </Stack>
                            </>
                        )}
                    </>
                ) : (
                    <Table>
                        <TableBody>
                            <NoDataRow text="No data for these filters" />
                        </TableBody>
                    </Table>
                )}

                <Dialog
                    maxWidth="lg"
                    onClose={() => setOpenHidePanel(false)}
                    open={openHidePanel}
                >
                    <DialogTitle>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            spacing={2}
                        >
                            <Typography variant="h6">
                                Show/Hide fields{" "}
                            </Typography>
                            <Tooltip title="Clean filters">
                                <IconButton
                                    onClick={() => {
                                        setJs_({
                                            ...js_,
                                            hideFields: [
                                                ...(js.hideFields ?? []),
                                            ],
                                        });
                                    }}
                                >
                                    <Icons.Clean />
                                </IconButton>
                            </Tooltip>
                            <IconButton onClick={() => setOpenHidePanel(false)}>
                                <Icons.CloseNavbar />
                            </IconButton>
                        </Stack>
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth
                            size="small"
                            label="Search metrics"
                            value={hideFieldsSearch}
                            onChange={(e) =>
                                setHideFieldsSearch(e.target.value)
                            }
                            sx={{ mb: 2 }}
                        />

                        <List>
                            {Object.entries(group.result ?? {})
                                .filter(([metric]) => {
                                    const search = hideFieldsSearch
                                        .trim()
                                        .toLowerCase();
                                    const helper = metrics[metric] ?? {};

                                    if (!search) return true;

                                    return [
                                        metric,
                                        helper.short,
                                        helper.tooltip === false
                                            ? ""
                                            : helper.tooltip,
                                        fns(
                                            hotelID,
                                            getMetricName(
                                                metric,
                                                place,
                                                metrics,
                                            ) ??
                                                helper.short ??
                                                metric,
                                            helper.headerFormat,
                                        ),
                                    ].some((e) =>
                                        String(e ?? "")
                                            .toLowerCase()
                                            .includes(search),
                                    );
                                })
                                .map(([metric, value]) => (
                                    <ListItem disablePadding key={metric}>
                                        <ListItemButton
                                            onClick={() => {
                                                js_.hideFields.includes(metric)
                                                    ? setJs_({
                                                          ...js_,
                                                          hideFields:
                                                              js_.hideFields.filter(
                                                                  (e) =>
                                                                      e !==
                                                                      metric,
                                                              ),
                                                      })
                                                    : setJs_({
                                                          ...js_,
                                                          hideFields: [
                                                              ...js_.hideFields,
                                                              metric,
                                                          ],
                                                      });
                                            }}
                                        >
                                            <ListItemIcon>
                                                {js_.hideFields.includes(
                                                    metric,
                                                ) ? (
                                                    <Icons.EyeOff />
                                                ) : (
                                                    <Icons.EyeOn color="primary" />
                                                )}
                                            </ListItemIcon>

                                            {(() => {
                                                const helper =
                                                    metrics[metric] ?? {};
                                                return (
                                                    <ListItemText
                                                        primary={
                                                            <Typography
                                                                variant="body1"
                                                                style={{
                                                                    fontWeight:
                                                                        helper.important
                                                                            ? "bold"
                                                                            : "normal",
                                                                }}
                                                            >
                                                                {helper.short ??
                                                                    metric}
                                                            </Typography>
                                                        }
                                                    />
                                                );
                                            })()}
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                        </List>
                    </DialogContent>
                </Dialog>
            </Container>
        </>
    );
};

export default ReportTableMetricsSummary;
